import { useCallback, useEffect, useRef, useState } from "react";
import { apiPcfEstoque, type EmpenhosMaterialResp, type LoteEmpenho } from "../lib/api";
import { MsgModal } from "./MsgModal";
import { PesquisaLotesModal } from "./PesquisaLotesModal";
import { useOrdenacao, ThOrdena } from "../hooks/useOrdenacao";
import { Confirma, Modal, enterTab, fN, paraInput, parseBR, soNum } from "./EstoqueUi";

// atcadpcf / tela-empenho-material "Cadastro de PCF - Empenho de Material" (botão Empenho da TELA-PCF, legado novo) +
// tela-emepenho-material-log "Histórico de Empenhos". Regras no servidor (queries/pcf-empenho.ts).

const casasDe = (u: string) => ((u || "").toUpperCase() === "KG" ? 4 : 0);

export function EmpenhoMaterialModal({ numpcf, onClose }: { numpcf: number; onClose: () => void }) {
  const [d, setD] = useState<EmpenhosMaterialResp | null>(null);
  const [numlot, setNumlot] = useState("");
  const [lote, setLote] = useState<LoteEmpenho | null>(null);
  const [qtd, setQtd] = useState("");
  const [seqemp, setSeqemp] = useState(0); // 0 = REGISTRO NOVO
  const [pode, setPode] = useState({ alterar: false, excluir: false });
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [pesq, setPesq] = useState(false);
  const [log, setLog] = useState(false);
  const [confExc, setConfExc] = useState(false);
  const [busy, setBusy] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const refLote = useRef<HTMLInputElement>(null);
  const refQtd = useRef<HTMLInputElement>(null);
  const ord = useOrdenacao(d?.itens, { lote: (x) => x.numlot, loc: (x) => x.nomloc, mat: (x) => x.nommat, qtd: (x) => x.qtdemp, fat: (x) => x.qtdfat, sit: (x) => x.situac });

  const carregar = useCallback(async () => {
    try {
      const r = await apiPcfEstoque.empenhos(numpcf);
      if (!r.ok) return setErro(r.erro ?? "PCF não encontrada.");
      setD(r);
    } catch (e) {
      setErro((e as Error).message);
    }
  }, [numpcf]);
  useEffect(() => {
    void carregar();
  }, [carregar]);

  // LIMPAR (tem:722-749)
  const limpar = () => {
    setNumlot("");
    setLote(null);
    setQtd("");
    setSeqemp(0);
    setPode({ alterar: false, excluir: false });
    setTimeout(() => refLote.current?.focus(), 30);
  };

  // MOSTRAR-LOTE (tem:628-688)
  async function mostrarLote(n: number) {
    if (!n) return;
    try {
      const l = await apiPcfEstoque.lote(numpcf, n);
      setNumlot(String(n));
      if (l.erro) {
        setLote(null);
        setErro(l.erro);
        setTimeout(() => refLote.current?.focus(), 30);
        return;
      }
      setLote(l);
      setSeqemp(0);
      setTimeout(() => refQtd.current?.focus(), 30);
    } catch (e) {
      setErro((e as Error).message);
    }
  }

  // TABELA-Click (tem:314-375)
  async function abrirRegistro(s: number) {
    try {
      const r = await apiPcfEstoque.registro(numpcf, s);
      if (!r.ok) return setErro(r.erro ?? "Empenho não encontrado.");
      setSeqemp(s);
      setLote(r.lote);
      setNumlot(String(r.lote.numlot));
      setQtd(paraInput(r.qtdemp, casasDe(r.lote.unidade)));
      setPode({ alterar: r.podeAlterar, excluir: r.podeExcluir });
      setTimeout(() => refQtd.current?.focus(), 30);
    } catch (e) {
      setErro((e as Error).message);
    }
  }

  async function gravar() {
    if (busy) return;
    setBusy(true);
    try {
      const q = parseBR(qtd);
      const r = seqemp ? await apiPcfEstoque.alterar(numpcf, seqemp, q) : await apiPcfEstoque.incluir(numpcf, Number(numlot) || 0, q);
      if (!r.ok) {
        setErro(r.erro ?? "Falha na gravação.");
        return;
      }
      setOk(seqemp ? "Empenho alterado." : "Empenho incluído.");
      await carregar();
      limpar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function excluir() {
    setConfExc(false);
    try {
      const r = await apiPcfEstoque.excluir(numpcf, seqemp);
      if (!r.ok) return setErro(r.erro ?? "Falha na exclusão.");
      await carregar();
      limpar();
    } catch (e) {
      setErro((e as Error).message);
    }
  }

  const bloq = !!d?.bloqueada;
  const casas = casasDe(lote?.unidade ?? "");
  return (
    <Modal titulo="Empenho de Material" sub={`PCF ${numpcf}${d ? ` · ${d.nomcli} · ${d.nompro}` : ""}`} largura={1100} onClose={onClose}>
      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />
      <MsgModal texto={ok} tipo="ok" onClose={() => setOk(null)} />
      <Confirma texto={confExc ? "Deseja realmente deletar o empenho?" : null} onSim={() => void excluir()} onNao={() => setConfExc(false)} />
      {bloq && <p className="pcf-hint">PCF {d?.situacPcf} — empenho de material somente para consulta.</p>}
      <div ref={raiz} onKeyDown={(e) => enterTab(e, raiz.current)}>
        <div className="pcf-grid">
          <label className="pcf-campo pcf-sm">
            <span>Lote</span>
            <input ref={refLote} className="num" inputMode="numeric" value={numlot} disabled={bloq || !!seqemp} autoFocus
              onChange={(e) => setNumlot(soNum(e.target.value, 0))}
              onBlur={() => Number(numlot) && Number(numlot) !== lote?.numlot && void mostrarLote(Number(numlot))} />
          </label>
          <button className="btn-secundario" tabIndex={-1} disabled={bloq || !!seqemp} onClick={() => setPesq(true)}>Pesquisar</button>
          <label className="pcf-campo pcf-lg"><span>Matéria Prima</span><input value={lote?.nommat ?? ""} readOnly tabIndex={-1} /></label>
          <label className="pcf-campo"><span>Local de Estoque</span><input value={lote?.nomloc ?? ""} readOnly tabIndex={-1} /></label>
          <label className="pcf-campo pcf-sm"><span>Unidade</span><input value={lote?.unidade ?? ""} readOnly tabIndex={-1} /></label>
        </div>
        <div className="pcf-grid">
          <label className="pcf-campo pcf-sm"><span>Comprimento</span><input value={lote?.comprimento ?? ""} readOnly tabIndex={-1} /></label>
          <label className="pcf-campo pcf-sm"><span>Qtd do Lote</span><input className="num" value={lote ? fN(lote.qtdLote, casas) : ""} readOnly tabIndex={-1} /></label>
          <label className="pcf-campo pcf-sm"><span>Qtd Utilizada</span><input className="num" value={lote ? fN(lote.qtdUtilizada, casas) : ""} readOnly tabIndex={-1} /></label>
          <label className="pcf-campo pcf-sm"><span>Qtd Empenhada</span><input className="num" value={lote ? fN(lote.qtdEmpenhada, casas) : ""} readOnly tabIndex={-1} /></label>
          <label className="pcf-campo pcf-sm"><span>Qtd Faturada</span><input className="num" value={lote ? fN(lote.qtdFaturada, casas) : ""} readOnly tabIndex={-1} /></label>
          <label className="pcf-campo pcf-sm"><span>Saldo Disponível</span><input className="num" value={lote ? fN(lote.saldo, casas) : ""} readOnly tabIndex={-1} /></label>
          <label className="pcf-campo pcf-sm">
            <span>Selecionado</span>
            <input ref={refQtd} className="num" inputMode="decimal" value={qtd} disabled={bloq || (!!seqemp && !pode.alterar)}
              onChange={(e) => setQtd(soNum(e.target.value, casas))} />
          </label>
        </div>
        <div className="pcf-acoes">
          <button className="btn-acao" disabled={bloq || busy || !!seqemp || !lote} onClick={() => void gravar()}>Incluir</button>
          <button className="btn-secundario" disabled={bloq || busy || !seqemp || !pode.alterar} onClick={() => void gravar()}>Alterar</button>
          <button className="btn-secundario plan-del" disabled={bloq || !seqemp || !pode.excluir} onClick={() => setConfExc(true)}>Excluir</button>
          <button className="btn-secundario" onClick={() => setLog(true)}>Histórico de Empenhos</button>
          <button className="btn-secundario" onClick={limpar}>Limpar</button>
        </div>
      </div>
      <div className="tabela-wrap" style={{ marginTop: 12, maxHeight: "40vh", overflow: "auto" }}>
        <table className="tabela-pcfs">
          <thead>
            <tr>
              <ThOrdena ord={ord} col="lote" className="num">Lote</ThOrdena>
              <ThOrdena ord={ord} col="loc">Local de Estoque</ThOrdena>
              <ThOrdena ord={ord} col="mat">Matéria Prima</ThOrdena>
              <ThOrdena ord={ord} col="qtd" className="num">Quantidade</ThOrdena>
              <ThOrdena ord={ord} col="fat" className="num">Qtd Utilizada</ThOrdena>
              <ThOrdena ord={ord} col="sit">Situação</ThOrdena>
            </tr>
          </thead>
          <tbody>
            {(d?.itens.length ?? 0) === 0 && (
              <tr><td colSpan={6} className="plan-vazio">Nenhum empenho de material.</td></tr>
            )}
            {ord.linhas.map((x) => (
              <tr key={x.seqemp} className={"row-clickable" + (x.seqemp === seqemp ? " sel" : "")} onClick={() => void abrirRegistro(x.seqemp)}>
                <td className="num">{x.numlot}</td>
                <td>{x.nomloc}</td>
                <td>{x.nommat}</td>
                <td className="num">{fN(x.qtdemp, casasDe(x.unidade))}</td>
                <td className="num">{fN(x.qtdfat, casasDe(x.unidade))}</td>
                <td>{x.situac}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {d && (
        <div className="pcf-acoes" style={{ marginTop: 8 }}>
          <span className="spacer" />
          <span>Total Selecionado: <b>{fN(d.totSel, 2)}</b></span>
          <span style={{ marginLeft: 18 }}>Total Utilizado: <b>{fN(d.totFat, 2)}</b></span>
        </div>
      )}
      {pesq && (
        <PesquisaLotesModal onFechar={() => setPesq(false)} onEscolher={(n) => { setPesq(false); void mostrarLote(n); }} />
      )}
      {log && <LogEmpenhos numpcf={numpcf} onClose={() => setLog(false)} />}
    </Modal>
  );
}

function LogEmpenhos({ numpcf, onClose }: { numpcf: number; onClose: () => void }) {
  const [r, setR] = useState<Awaited<ReturnType<typeof apiPcfEstoque.log>> | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const ord = useOrdenacao(r?.linhas, { dh: (x) => x.dataHora, his: (x) => x.historico, usu: (x) => x.usuario });
  useEffect(() => {
    apiPcfEstoque.log(numpcf).then((x) => {
      setR(x);
      if (x.truncado) setErro("Quantidade de Itens listados é superior ao máximo permitido.");
    }).catch((e) => setErro((e as Error).message));
  }, [numpcf]);
  return (
    <Modal titulo="Histórico de Empenhos" sub={`PCF : ${numpcf}   Cliente : ${r?.nomcli ?? ""}`} largura={1000} onClose={onClose}>
      <MsgModal texto={erro} tipo="aviso" onClose={() => setErro(null)} />
      <div className="tabela-wrap" style={{ maxHeight: "60vh", overflow: "auto" }}>
        <table className="tabela-pcfs">
          <thead>
            <tr>
              <ThOrdena ord={ord} col="dh">Data e Hora</ThOrdena>
              <ThOrdena ord={ord} col="his">Histórico</ThOrdena>
              <ThOrdena ord={ord} col="usu">Usuário</ThOrdena>
            </tr>
          </thead>
          <tbody>
            {(r?.linhas.length ?? 0) === 0 && <tr><td colSpan={3} className="plan-vazio">Nenhum registro.</td></tr>}
            {ord.linhas.map((x, i) => (
              <tr key={i}><td>{x.dataHora}</td><td>{x.historico}</td><td>{x.usuario}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
